import { useDebounceCallback } from 'usehooks-ts';
import { GroupBase, OptionsOrGroups } from 'react-select';
import {
  getBounds,
  ComboBoxPrediction,
  MESSAGES,
  InputErrorMessage,
} from '~/utilities';
import { useLocationServices } from '../useLocationServices';
import { useState } from 'react';

import { hasFeatureFlag } from '@janeapp/feature-flags-client';
type GeocodedData = {
  coordinates: { lat: number; lng: number };
};

type PredictionResult = {
  predictions: ComboBoxPrediction[];
  error: InputErrorMessage | null;
};

const MIN_INPUT_LENGTH = 3;

// convert google prediction + geocoded data to simple format
const createPrediction = (
  prediction: google.maps.places.AutocompletePrediction,
  geocoded: GeocodedData
): ComboBoxPrediction => ({
  value: prediction.place_id,
  label: prediction.description,
  geocoded,
});

// map places api status to our error messages
const getErrorFromStatus = (
  status: google.maps.places.PlacesServiceStatus
): InputErrorMessage | null => {
  const statusMap: Record<string, InputErrorMessage> = {
    [google.maps.places.PlacesServiceStatus.INVALID_REQUEST]:
      MESSAGES.invalidRequest,
    [google.maps.places.PlacesServiceStatus.NOT_FOUND]: MESSAGES.notFound,
    [google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT]:
      MESSAGES.overQueryLimit,
    [google.maps.places.PlacesServiceStatus.REQUEST_DENIED]:
      MESSAGES.requestDenied,
    [google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR]:
      MESSAGES.unknownError,
    [google.maps.places.PlacesServiceStatus.ZERO_RESULTS]: MESSAGES.zeroResults,
  };

  return statusMap[status] || null;
};

const isWithinServiceBounds = (coordinates: {
  lat: number;
  lng: number;
}): boolean => {
  const bounds = getBounds();
  return (
    coordinates.lat >= bounds.south &&
    coordinates.lat <= bounds.north &&
    coordinates.lng >= bounds.west &&
    coordinates.lng <= bounds.east
  );
};

export function includeGeocoderResult(
  result: google.maps.GeocoderResult
): boolean {
  // BC geo bounds include some parts of Alberta and the US.
  // Need to only include BC addresses when ALL_BC_BETA feature flag is enabled.
  if (hasFeatureFlag('ALL_BC_BETA') === false) {
    return true;
  }

  if (result == null) return false;
  const province = result.address_components
    .filter((c) => c.types.includes('administrative_area_level_1'))
    .at(0);

  if (province && province.short_name !== 'BC') {
    return false;
  }
  return true;
}

export const useAutocompleteService = () => {
  const {
    autocompleteService,
    geocoder,
    isLoading,
    error: serviceError,
    isReady,
  } = useLocationServices();
  const [error, setError] = useState<InputErrorMessage | null>(null);

  // geocodes a prediction and return geocoded data if within bounds
  // lat/lng values are not immediately availoable on the prediction object
  // so we need to use the geocoder
  const geocodePrediction = async (
    prediction: google.maps.places.AutocompletePrediction
  ): Promise<ComboBoxPrediction | null> => {
    try {
      //safe no-null assertion, this code will not run if geocoder isnt loaded
      const geocodeResult = await geocoder!.geocode({
        placeId: prediction.place_id,
      });

      const result = geocodeResult.results[0];
      if (!result) return null;

      if (includeGeocoderResult(result) === false) {
        return null;
      }

      const { lat, lng } = result.geometry.location;
      const coordinates = { lat: lat(), lng: lng() };
      const geocodedData: GeocodedData = {
        coordinates,
      };

      // only return if within supported area
      return isWithinServiceBounds(coordinates)
        ? createPrediction(prediction, geocodedData)
        : null;
    } catch {
      // skip predictions that fail to geocode
      return null;
    }
  };

  const geocodeAndFilterPredictions = async (
    predictions: google.maps.places.AutocompletePrediction[]
  ): Promise<ComboBoxPrediction[]> => {
    if (!predictions.length) return [];

    const geocodingPromises = predictions.map(geocodePrediction);
    const geocodedResults = await Promise.all(geocodingPromises);
    const validResults = geocodedResults.filter(
      (result) => result !== null
    ) as ComboBoxPrediction[];

    // throw error if all predictions were filtered out by the bounds filter
    if (predictions.length > 0 && validResults.length === 0) {
      throw new Error(MESSAGES.serviceAreaError.userMessage);
    }

    return validResults;
  };

  const getPlacePredictions = (
    input: string
  ): Promise<{
    predictions: google.maps.places.AutocompletePrediction[];
    status: google.maps.places.PlacesServiceStatus;
  }> => {
    return new Promise((resolve) => {
      //safe no-null assertion, this code will not run if autocompleteService isnt loaded
      autocompleteService!.getPlacePredictions(
        {
          input,
          // Restriction is not 100%, if there is a direct route to a location, it will still return the location
          // So that is why we need to manually filter the predictions by mapBounds
          locationRestriction: getBounds(),
          componentRestrictions: { country: 'CA' },
          region: 'CA',
          // Restricts autocomplete to return geocode results only and not to return business
          // see https://developers.google.com/maps/documentation/javascript/legacy/supported_types
          types: ['geocode'],
        },
        (predictions, status) => {
          resolve({
            predictions: predictions || [],
            status,
          });
        }
      );
    });
  };

  const fetchPredictions = async (input: string): Promise<PredictionResult> => {
    const { predictions, status } = await getPlacePredictions(input);

    const apiError = getErrorFromStatus(status);
    if (apiError) {
      return {
        predictions: [],
        error: apiError,
      };
    }

    // Geocode and filter predictions
    try {
      const filteredPredictions =
        await geocodeAndFilterPredictions(predictions);
      return {
        predictions: filteredPredictions,
        error: null,
      };
    } catch (error) {
      // handle service area filtering errors
      if (
        error instanceof Error &&
        error.message.includes(MESSAGES.serviceAreaError.userMessage)
      ) {
        return {
          predictions: [],
          error: MESSAGES.serviceAreaError,
        };
      }

      // handle unexpected errors
      return {
        predictions: [],
        error: MESSAGES.unknownError,
      };
    }
  };

  const loadPredictions = (
    inputValue: string,
    callback: (
      options: OptionsOrGroups<
        ComboBoxPrediction,
        GroupBase<ComboBoxPrediction>
      >
    ) => void
  ) => {
    if (!inputValue || inputValue.length < MIN_INPUT_LENGTH || !isReady) {
      callback([]);
      return;
    }

    fetchPredictions(inputValue)
      .then((result) => {
        if (result.error) {
          setError(result.error);
        }

        // always call callback with predictions (empty array shows NoOptionsMessage)
        callback(result.predictions);
      })
      .catch(() => {
        callback([]);
      });
  };

  const loadPredictionsDebounced = useDebounceCallback(loadPredictions, 300);

  return {
    isLoading,
    loadPredictionsDebounced,
    error: serviceError || error,
    setError,
  };
};
